import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useApi } from "../../api/ApiProvider";
import {
  PunchRequestDtoActionEnum,
  PunchResultDto,
  UserDto,
} from "../../api-client";
import useStore from "../../helpers/useStore";
import { UserContext } from "../../stores/User/User.provider";
import { queryClient } from "../../stores/queryClient";
import { calendarKeys } from "../../stores/Calendar/calendar.keys";
import notificationStore from "../../stores/Notification/notificationStore";

const APP_TIME_ZONE = "Europe/Berlin";
const localDate = (date: Date) =>
  date.toLocaleDateString("en-CA", { timeZone: APP_TIME_ZONE });

/**
 * On the clock while the stored punch-in falls on today's local date — the
 * same rule the API applies. A forgotten punch-out expires at midnight.
 */
export const isPunchedInToday = (user: UserDto | null | undefined): boolean =>
  !!user?.punchInTime &&
  localDate(new Date(user.punchInTime)) === localDate(new Date());

const pad = (n: number) => String(n).padStart(2, "0");
const toTimeInput = (date: Date) =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;
const fromTimeInput = (value: string): Date | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date();
  date.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return date;
};
const formatTime = (value?: string | Date | null) =>
  value ? toTimeInput(new Date(value)) : "—";
const formatMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h} Std ${m} Min` : `${m} Min`;
};

/** Quick lengths offered when the day has nothing planned to shift. */
const DURATION_OPTIONS = [1, 2, 4, 8];
type DurationChoice = number | "custom" | "unknown";

interface PunchClockDialogProps {
  open: boolean;
  action: "in" | "out";
  onClose: () => void;
}

/**
 * Punch in / out (avatar menu, `punchIn` permission). The time defaults to
 * now and can be changed. Every change is previewed with a dry run, so the
 * worked time and whether to ask for a duration come from the API's own rules
 * (break handling, § 4 ArbZG) rather than being recomputed here.
 */
export const PunchClockDialog: React.FC<PunchClockDialogProps> = observer(
  ({ open, action, onClose }) => {
    const { workingHours } = useApi();
    const userStore = useStore(UserContext);

    const [time, setTime] = useState("");
    const [durationChoice, setDurationChoice] =
      useState<DurationChoice>("unknown");
    const [customHours, setCustomHours] = useState("");
    const [preview, setPreview] = useState<PunchResultDto | null>(null);
    const [error, setError] = useState<string | null>(null);
    // A failed confirm must not block retrying, unlike a refused preview.
    const [saveError, setSaveError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      if (!open) return;
      setTime(toTimeInput(new Date()));
      setDurationChoice("unknown");
      setCustomHours("");
      setPreview(null);
      setError(null);
      setSaveError(null);
    }, [open, action]);

    /**
     * A refusal (400) usually means the punched-in state changed elsewhere —
     * another tab or device. Reload the user so the menu offers the right action.
     */
    const refreshUserOnRefusal = (e: any) => {
      if (e?.status === 400) userStore.getUser();
    };

    const at = fromTimeInput(time);
    const durationMinutes = (() => {
      if (durationChoice === "unknown") return undefined;
      if (durationChoice === "custom") {
        const hours = Number(customHours.replace(",", "."));
        return hours > 0 ? Math.round(hours * 60) : undefined;
      }
      return durationChoice * 60;
    })();

    const request = (dryRun: boolean) => ({
      action:
        action === "in"
          ? PunchRequestDtoActionEnum.In
          : PunchRequestDtoActionEnum.Out,
      at: at!.toISOString(),
      durationMinutes: action === "in" ? durationMinutes : undefined,
      dryRun,
    });

    useEffect(() => {
      if (!open || !at) return;
      let cancelled = false;
      // Loading from the first change on, so the confirm waits for the preview
      // of what is actually entered.
      setLoading(true);
      setSaveError(null);
      const timer = setTimeout(async () => {
        try {
          const result = await workingHours.punch(request(true));
          if (!cancelled) {
            setPreview(result);
            setError(null);
          }
        } catch (e: any) {
          if (!cancelled) {
            setPreview(null);
            setError(e?.message || "Vorschau konnte nicht berechnet werden.");
            refreshUserOnRefusal(e);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      }, 300);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, action, time, durationMinutes]);

    const handleConfirm = async () => {
      if (!at) return;
      setSaving(true);
      try {
        const result = await workingHours.punch(request(false));
        if (userStore.user) {
          userStore.setUser({
            ...userStore.user,
            punchInTime: result.punchInTime ?? null,
          });
        }
        queryClient.invalidateQueries({
          queryKey: calendarKeys.workingHours.all(),
        });
        notificationStore.showMessage(
          action === "in"
            ? `Eingestempelt um ${formatTime(result.at)}`
            : `Ausgestempelt um ${formatTime(result.at)} — ${formatMinutes(result.netWorkMinutes)} gearbeitet`,
          "success",
        );
        onClose();
      } catch (e: any) {
        setSaveError(e?.message || "Stempeln fehlgeschlagen.");
        refreshUserOnRefusal(e);
      } finally {
        setSaving(false);
      }
    };

    const title = action === "in" ? "Einstempeln" : "Ausstempeln";

    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label={action === "in" ? "Einstempelzeit" : "Ausstempelzeit"}
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
            sx={{ mb: 2 }}
          />

          {action === "in" && preview?.needsDuration && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Für heute ist keine Arbeitszeit geplant. Wie lange möchten Sie
                arbeiten?
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={durationChoice}
                onChange={(_, value) => {
                  if (value === null) return;
                  setDurationChoice(value);
                  setCustomHours("");
                }}
                sx={{ flexWrap: "wrap", mb: 1 }}
              >
                {DURATION_OPTIONS.map((hours) => (
                  <ToggleButton key={hours} value={hours}>
                    {hours} Std
                  </ToggleButton>
                ))}
                <ToggleButton value="unknown">Weiß noch nicht</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                label="Andere Dauer (Stunden)"
                size="small"
                value={customHours}
                onChange={(e) => {
                  setCustomHours(e.target.value);
                  setDurationChoice(e.target.value ? "custom" : "unknown");
                }}
                inputProps={{ inputMode: "decimal" }}
                fullWidth
              />
              {durationChoice === "unknown" && (
                <Typography variant="caption" color="text.secondary">
                  Ohne Angabe wird zunächst 1 Stunde eingetragen.
                </Typography>
              )}
            </Box>
          )}

          {(error || saveError) && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error || saveError}
            </Alert>
          )}

          {preview && !error && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {loading && <CircularProgress size={14} />}
              {action === "in" ? (
                <Typography variant="body2">
                  Arbeitszeit heute: {formatTime(preview.workFrom)} –{" "}
                  {formatTime(preview.workTo)} (
                  {formatMinutes(preview.netWorkMinutes)} ohne Pausen)
                </Typography>
              ) : (
                <Typography variant="body2">
                  Eingestempelt um {formatTime(preview.workFrom)} — gearbeitet:{" "}
                  <strong>{formatMinutes(preview.netWorkMinutes)}</strong>{" "}
                  (Pausen abgezogen)
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!at || saving || loading || !!error || !preview}
          >
            {title}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);
